import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Markdown from '../components/Markdown';
import Verdict, { formatDay } from '../components/Verdict';

function formatSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function LinkItem({ link }) {
  const label = link.label || link.ref;
  const relation = link.relation ? <span className="text-[#555555]">{link.relation} · </span> : null;
  const cls = 'text-white/80 hover:text-white underline decoration-[#333333] underline-offset-4 hover:decoration-white';
  if (link.type === 'runtime') {
    return <li>{relation}<span className="text-[#555555]">runtime · </span><Link className={cls} to={`/runtimes/${link.ref}`}>soma {label}</Link></li>;
  }
  if (link.type === 'checkpoint') {
    return <li>{relation}<span className="text-[#555555]">checkpoint · </span><Link className={cls} to={`/checkpoints/${link.ref}`}>{label}</Link></li>;
  }
  if (link.type === 'development') {
    return <li>{relation}<span className="text-[#555555]">development · </span><Link className={cls} to={`/developments/${link.ref}`}>{label}</Link></li>;
  }
  return <li>{relation}<a className={cls} href={link.ref} target="_blank" rel="noopener noreferrer">{label}</a></li>;
}

function citation(d) {
  const year = (d.occurred_on || d.published_at || '').slice(0, 4);
  const authors = (d.authors && d.authors.length ? d.authors : ['logOS']).join(' and ');
  const url = `https://logossoma.com/developments/${d.slug}?version=${d.version}`;
  return {
    plain: `${(d.authors || []).join(', ') || 'logOS'} (${year}). ${d.title}. logOS developments, version ${d.version}. ${url}`,
    bibtex: `@misc{logos_${d.slug.replace(/-/g, '_')}_v${d.version},\n  author = {${authors}},\n  title = {${d.title}},\n  year = {${year}},\n  howpublished = {logOS developments, version ${d.version}},\n  url = {${url}}\n}`,
  };
}

/** One lab-notebook entry, at its newest version or the one asked for. */
export default function Development() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const version = params.get('version');
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    setEntry(null);
    fetch(`/api/developments/${encodeURIComponent(slug)}${version ? `?version=${encodeURIComponent(version)}` : ''}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('this development was not found'))))
      .then((d) => { setEntry(d); document.title = `${d.title} · logOS`; })
      .catch((e) => setError(e.message));
  }, [slug, version]);

  if (error) {
    return (
      <div className="min-h-screen bg-black py-16 px-6 page-wrapper">
        <div className="max-w-3xl mx-auto text-[#aa4444] font-mono lowercase">{error}</div>
      </div>
    );
  }
  if (!entry) {
    return <div className="min-h-screen bg-black py-16 px-6 page-wrapper"><div className="max-w-3xl mx-auto text-[#666666] lowercase animate-pulse">loading…</div></div>;
  }

  const artifacts = Object.fromEntries(entry.files.map((f) => [f.name, f.url]));
  const cite = citation(entry);
  const copy = (kind) => {
    navigator.clipboard?.writeText(cite[kind]).then(() => { setCopied(kind); setTimeout(() => setCopied(''), 1500); });
  };
  const outdated = entry.version !== entry.latest_version;

  return (
    <div className="min-h-screen bg-black text-white page-wrapper" data-testid="development-page">
      <div className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
        <Link to="/developments" className="text-[#555555] hover:text-white transition-colors lowercase text-sm font-mono">
          ← developments
        </Link>

        <header className="mt-8 space-y-4">
          <div className="flex items-baseline gap-2 flex-wrap text-xs font-mono lowercase">
            <Verdict verdict={entry.verdict} />
            <span className="text-[#777777]">{entry.kind}</span>
            {entry.thread && (
              <Link to={`/developments?thread=${encodeURIComponent(entry.thread)}`} className="text-[#777777] hover:text-white">
                · {entry.thread}
              </Link>
            )}
            <span className="text-[#555555] ml-auto">
              {formatDay(entry.occurred_on)} · version {entry.version}
            </span>
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-white lowercase leading-tight" data-testid="development-title">
            {entry.title}
          </h1>
          {entry.authors?.length > 0 && <p className="text-[#888888] text-sm lowercase">{entry.authors.join(', ')}</p>}
          {outdated && (
            <p className="text-xs font-mono lowercase text-[#b0a070] border border-[#3a3520] rounded px-3 py-2">
              you are reading version {entry.version}.{' '}
              <Link to={`/developments/${entry.slug}`} className="underline underline-offset-4">version {entry.latest_version} is the newest →</Link>
            </p>
          )}
          {entry.summary && (
            <p className="text-[#cccccc] leading-relaxed border-l-2 border-[#3a413a] pl-4" data-testid="development-summary">
              {entry.summary}
            </p>
          )}
          {entry.evidence && <p className="text-[#666666] text-xs font-mono lowercase">evidence · {entry.evidence}</p>}
          {entry.links?.length > 0 && (
            <ul className="text-xs font-mono lowercase space-y-1 pt-1" data-testid="development-links">
              {entry.links.map((l, i) => <LinkItem key={i} link={l} />)}
            </ul>
          )}
        </header>

        <article className="mt-10 space-y-4" data-testid="development-body">
          <Markdown artifacts={artifacts}>{entry.body_md}</Markdown>
        </article>

        {entry.files.length > 0 && (
          <section className="mt-12 space-y-2" data-testid="development-files">
            <h2 className="text-xs lowercase text-[#666666] tracking-wider font-mono">files · {entry.files.length}</h2>
            <ul className="border border-[#1e251f] rounded-xl p-4 space-y-1.5 text-xs font-mono">
              {entry.files.map((f) => (
                <li key={f.name} className="flex items-baseline justify-between gap-3 flex-wrap">
                  <a href={f.url} className="text-white/80 hover:text-white underline decoration-[#333333] underline-offset-4">{f.name}</a>
                  <span className="text-[#555555] lowercase">
                    {f.kind} · {formatSize(f.bytes)} · <span title={`sha256 ${f.sha256}`}>sha256 {f.sha256.slice(0, 12)}…</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-12 grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <h2 className="text-xs lowercase text-[#666666] tracking-wider font-mono">versions</h2>
            <ul className="text-xs font-mono lowercase space-y-1">
              {entry.versions.map((v) => (
                <li key={v.version} className="flex items-baseline gap-2">
                  <Link
                    to={`/developments/${entry.slug}?version=${v.version}`}
                    className={v.version === entry.version ? 'text-white' : 'text-[#777777] hover:text-white'}
                  >
                    v{v.version}
                  </Link>
                  <Verdict verdict={v.verdict} />
                  <span className="text-[#555555]">{formatDay(v.published_at)}</span>
                </li>
              ))}
            </ul>
            <a href={entry.markdown_url} className="inline-block text-xs font-mono text-[#777777] hover:text-white pt-1">markdown ↗</a>
          </div>
          <div className="space-y-2" data-testid="development-cite">
            <h2 className="text-xs lowercase text-[#666666] tracking-wider font-mono">cite</h2>
            <p className="text-xs text-[#999999] leading-relaxed">{cite.plain}</p>
            <div className="flex gap-3 text-xs font-mono lowercase">
              <button onClick={() => copy('plain')} className="text-[#777777] hover:text-white">{copied === 'plain' ? 'copied' : 'copy'}</button>
              <button onClick={() => copy('bibtex')} className="text-[#777777] hover:text-white">{copied === 'bibtex' ? 'copied' : 'copy bibtex'}</button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
